// Per-blockchainType helpers for the Contract Address admin forms.
// blockchainType comes from BlockchainNetwork.blockchainType (see AdminMerchantApi
// BLOCKCHAIN_TYPE_ENUM: EVM | SOLANA | TRON | TON | BITCOIN).

export interface AddressFormatInfo {
  placeholder: string;
  example: string;
  regex?: RegExp;
  description: string;
}

const ADDRESS_FORMAT_BY_TYPE: Record<string, AddressFormatInfo> = {
  EVM: {
    placeholder: '0x...',
    example: '0xdAC17F958D2ee523a2206206994597C13D831ec',
    regex: /^0x[a-fA-F0-9]{40}$/,
    description: 'EVM contract addresses start with 0x followed by 40 hex characters.',
  },
  SOLANA: {
    placeholder: 'e.g. EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
    example: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
    regex: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
    description: 'Solana token addresses are base58-encoded, 32-44 characters.',
  },
  TRON: {
    placeholder: 'e.g. TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
    example: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
    regex: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
    description: "Tron contract addresses start with 'T' followed by 33 base58 characters.",
  },
  TON: {
    placeholder: 'e.g. EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs',
    example: 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs',
    regex: /^[A-Za-z0-9_-]{48}$/,
    description: 'TON jetton addresses are 48 base64url characters.',
  },
  BITCOIN: {
    placeholder: 'native',
    example: 'native',
    description: 'Bitcoin has no smart-contract/token standard — only the native BTC entry is supported.',
  },
};

const DEFAULT_FORMAT: AddressFormatInfo = {
  placeholder: 'Enter contract address',
  example: '',
  description: '',
};

export function getAddressFormatInfo(blockchainType?: string | null): AddressFormatInfo {
  if (!blockchainType) return DEFAULT_FORMAT;
  return ADDRESS_FORMAT_BY_TYPE[blockchainType] || DEFAULT_FORMAT;
}

// Blockchain types that have no token/contract standard — only a native asset entry makes sense.
export const BLOCKCHAIN_TYPES_WITHOUT_CONTRACTS = ['BITCOIN'];

export function supportsTokenContracts(blockchainType?: string | null): boolean {
  if (!blockchainType) return true;
  return !BLOCKCHAIN_TYPES_WITHOUT_CONTRACTS.includes(blockchainType);
}

// Returns an error message if invalid, or undefined if valid/unknown-type (skip validation).
export function validateContractAddressFormat(
  blockchainType: string | null | undefined,
  address: string
): string | undefined {
  if (!blockchainType) return undefined;
  const format = ADDRESS_FORMAT_BY_TYPE[blockchainType];
  if (!format?.regex) return undefined;
  if (!format.regex.test(address)) {
    return `Invalid address format for this network. ${format.description}`;
  }
  return undefined;
}
